/**
 * InvoiceListView - Table/List view for invoices with sortable columns and selection
 *
 * Phase 13 Cluster A, Feature B: middle columns (incl. customer/Kunde) are
 * driven by a column-def array so they can be hidden/reordered via per-user
 * localStorage prefs. Only the selection column and the actions column are
 * pinned.
 */
import { Fragment, useMemo, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { Invoice } from '../../hooks/useInvoices';
import type { ColumnPrefs } from '../../hooks/useInvoiceColumnPrefs';
import { formatCurrency, formatDate, formatQuarter, getQuarter } from '@armard/shared';
import { FileText, Pencil, ChevronUp, ChevronDown } from 'lucide-react';

export type InvoiceGroupBy = 'none' | 'quarter';

type SortField = 'invoiceNumber' | 'invoiceDate' | 'dueDate' | 'grossTotal' | 'status' | 'createdAt' | 'paymentDate';
type SortOrder = 'asc' | 'desc';

interface ColumnDef {
  key: string;
  label: string;
  sortField?: SortField;
  align?: 'left' | 'right';
  cellClass?: string;
  cell: (invoice: Invoice, ctx: { isOverdue: boolean; displayStatus: Invoice['status'] | 'overdue' }) => ReactNode;
}

export const INVOICE_COLUMN_DEFS: readonly ColumnDef[] = [
  {
    key: 'invoiceNumber',
    label: 'Nr.',
    sortField: 'invoiceNumber',
    cell: (invoice) => (
      <span className="text-white font-medium">{invoice.invoiceNumber}</span>
    ),
  },
  {
    key: 'customer',
    label: 'Kunde',
    cell: (invoice) => (
      <div className="text-white/70 truncate max-w-xs">
        {invoice.customer?.companyName || 'Kunde'}
      </div>
    ),
  },
  {
    key: 'invoiceDate',
    label: 'Datum',
    sortField: 'invoiceDate',
    cell: (invoice) => (
      <div className="text-white/60 text-sm">{formatDate(invoice.invoiceDate)}</div>
    ),
  },
  {
    key: 'dueDate',
    label: 'Fällig',
    sortField: 'dueDate',
    cell: (invoice, { isOverdue }) => (
      <div className={`text-sm ${isOverdue ? 'text-red-400' : 'text-white/60'}`}>
        {formatDate(invoice.dueDate)}
      </div>
    ),
  },
  {
    key: 'netTotal',
    label: 'Netto',
    align: 'right',
    cellClass: 'text-right',
    cell: (invoice) => (
      <div className="text-white/60 text-sm">
        {formatCurrency(parseFloat(invoice.netTotal))}
      </div>
    ),
  },
  {
    key: 'grossTotal',
    label: 'Brutto',
    sortField: 'grossTotal',
    align: 'right',
    cellClass: 'text-right',
    cell: (invoice) => (
      <div className="text-white font-semibold">
        {formatCurrency(parseFloat(invoice.grossTotal))}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortField: 'status',
    cell: (_invoice, { displayStatus }) => <InvoiceStatusBadge status={displayStatus} />,
  },
  {
    key: 'paymentDate',
    label: 'Bezahlt',
    sortField: 'paymentDate',
    cell: (invoice) => (
      <div className="text-white/60 text-sm">
        {invoice.paymentDate ? formatDate(invoice.paymentDate) : '—'}
      </div>
    ),
  },
];

export const DEFAULT_INVOICE_COLUMN_ORDER: readonly string[] = INVOICE_COLUMN_DEFS.map(
  (c) => c.key
);

interface InvoiceListViewProps {
  invoices: Invoice[];
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSortChange?: (sortBy: SortField, sortOrder: SortOrder) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onSelectAll?: () => void;
  columnPrefs?: ColumnPrefs;
  groupBy?: InvoiceGroupBy;
}

interface SortableHeaderProps {
  label: string;
  field: SortField;
  currentSort?: SortField;
  currentOrder?: SortOrder;
  onSort?: (field: SortField, order: SortOrder) => void;
  align?: 'left' | 'right';
}

function SortableHeader({ label, field, currentSort, currentOrder, onSort, align = 'left' }: SortableHeaderProps) {
  const isActive = currentSort === field;

  const handleClick = () => {
    if (!onSort) return;
    if (isActive) {
      onSort(field, currentOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSort(field, field === 'grossTotal' || field === 'invoiceDate' || field === 'dueDate' || field === 'paymentDate' ? 'desc' : 'asc');
    }
  };

  return (
    <th
      className={`${align === 'right' ? 'text-right' : 'text-left'} px-6 py-3 text-xs font-medium uppercase tracking-wider select-none ${
        onSort ? 'cursor-pointer hover:text-white/70 transition-colors' : ''
      } ${isActive ? 'text-white/80' : 'text-white/40'}`}
      onClick={handleClick}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          currentOrder === 'asc'
            ? <ChevronUp className="w-3 h-3" />
            : <ChevronDown className="w-3 h-3" />
        )}
      </span>
    </th>
  );
}

interface QuarterGroup {
  key: string;
  quarter: 1 | 2 | 3 | 4;
  year: number;
  invoices: Invoice[];
  totalNet: number;
  totalGross: number;
  totalOpen: number;
}

// Invoices whose gross amount counts as outstanding receivables. Matches
// the dashboard's `OutstandingInvoicesWidget` semantics: only issued,
// collectible invoices. Drafts are not yet sent, deferred invoices are
// intentionally paused, and paid/cancelled are closed out.
const OPEN_STATUSES: ReadonlySet<Invoice['status']> = new Set([
  'sent',
  'overdue',
]);

export function InvoiceListView({
  invoices,
  sortBy,
  sortOrder,
  onSortChange,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  columnPrefs,
  groupBy = 'none',
}: InvoiceListViewProps) {
  const navigate = useNavigate();
  const hasSelection = selectedIds !== undefined;
  const allSelected = hasSelection && invoices.length > 0 && selectedIds.size === invoices.length;

  // Resolve which middle columns to render, in order.
  const defByKey = new Map(INVOICE_COLUMN_DEFS.map((d) => [d.key, d]));
  const order = columnPrefs?.order ?? DEFAULT_INVOICE_COLUMN_ORDER;
  const hidden = new Set(columnPrefs?.hidden ?? []);
  const middleColumns = order
    .map((key) => defByKey.get(key))
    .filter((d): d is ColumnDef => !!d && !hidden.has(d.key));

  const quarterGroups = useMemo<QuarterGroup[] | null>(() => {
    if (groupBy !== 'quarter') return null;
    const map = new Map<string, QuarterGroup>();
    for (const invoice of invoices) {
      const { quarter, year } = getQuarter(invoice.invoiceDate);
      const key = `${year}-${quarter}`;
      let group = map.get(key);
      if (!group) {
        group = {
          key,
          quarter,
          year,
          invoices: [],
          totalNet: 0,
          totalGross: 0,
          totalOpen: 0,
        };
        map.set(key, group);
      }
      const gross = parseFloat(invoice.grossTotal);
      group.invoices.push(invoice);
      group.totalNet += parseFloat(invoice.netTotal);
      group.totalGross += gross;
      if (OPEN_STATUSES.has(invoice.status)) {
        group.totalOpen += gross;
      }
    }
    // Order groups chronologically (newest first) regardless of how the
    // underlying invoice list is sorted. Invoices within each group keep
    // the sort order from the server response.
    return Array.from(map.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.quarter - a.quarter;
    });
  }, [invoices, groupBy]);

  const totalColumns =
    (hasSelection ? 1 : 0) + middleColumns.length + 1; /* actions */

  const handleRowClick = (invoiceId: string) => {
    navigate({ to: '/invoices/$id', params: { id: invoiceId } });
  };

  const handleEdit = (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    navigate({ to: '/invoices/$id/edit', params: { id: invoiceId } });
  };

  const handlePdfDownload = (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    window.open(`/api/v1/invoices/${invoiceId}/pdf`, '_blank');
  };

  const handleCheckboxClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onToggleSelect?.(id);
  };

  const renderInvoiceRow = (invoice: Invoice) => {
    const isOverdue = invoice.status === 'sent' && new Date(invoice.dueDate) < new Date();
    const displayStatus = isOverdue ? 'overdue' : invoice.status;
    const isSelected = selectedIds?.has(invoice.id) ?? false;

    return (
      <tr
        key={invoice.id}
        onClick={() => handleRowClick(invoice.id)}
        className={`hover:bg-white/2 transition-colors cursor-pointer ${
          isSelected ? 'bg-white/5' : ''
        }`}
      >
        {hasSelection && (
          <td className="w-10 px-3 py-4">
            <input
              type="checkbox"
              checked={isSelected}
              onClick={(e) => handleCheckboxClick(e, invoice.id)}
              onChange={() => {}}
              className="w-5 h-5 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer"
            />
          </td>
        )}
        {middleColumns.map((c) => (
          <td key={c.key} className={`px-6 py-4 ${c.cellClass ?? ''}`}>
            {c.cell(invoice, { isOverdue, displayStatus })}
          </td>
        ))}
        <td className="px-6 py-4 text-right">
          <div className="flex items-center justify-end gap-1">
            {invoice.status === 'draft' && (
              <button
                onClick={(e) => handleEdit(e, invoice.id)}
                className="p-2 rounded-lg hover:bg-white/8 transition-colors"
                title="Bearbeiten"
              >
                <Pencil className="w-4 h-4 text-white/40 hover:text-white/70" />
              </button>
            )}
            <button
              onClick={(e) => handlePdfDownload(e, invoice.id)}
              className="p-2 rounded-lg hover:bg-white/8 transition-colors"
              title="PDF herunterladen"
            >
              <FileText className="w-4 h-4 text-white/40 hover:text-white/70" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white/3 border border-white/6 rounded-xl backdrop-blur-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/6">
              {hasSelection && (
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={onSelectAll}
                    className="w-5 h-5 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer"
                  />
                </th>
              )}
              {middleColumns.map((c) =>
                c.sortField ? (
                  <SortableHeader
                    key={c.key}
                    label={c.label}
                    field={c.sortField}
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={onSortChange}
                    align={c.align}
                  />
                ) : (
                  <th
                    key={c.key}
                    className={`${c.align === 'right' ? 'text-right' : 'text-left'} px-6 py-3 text-xs font-medium text-white/40 uppercase tracking-wider`}
                  >
                    {c.label}
                  </th>
                )
              )}
              <th className="text-right px-6 py-3 text-xs font-medium text-white/40 uppercase tracking-wider w-24">
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/6">
            {quarterGroups
              ? quarterGroups.map((group) => (
                  <Fragment key={group.key}>
                    <tr className="bg-white/[0.04] sticky top-0 z-10">
                      <td
                        colSpan={totalColumns}
                        className="px-6 py-2.5 text-xs font-medium uppercase tracking-wider"
                      >
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <span className="text-white/80">
                            {formatQuarter(group.quarter, group.year)}
                            <span className="text-white/40 ml-2 normal-case">
                              · {group.invoices.length} Rechnung
                              {group.invoices.length !== 1 ? 'en' : ''}
                            </span>
                          </span>
                          <span className="text-white/60 normal-case">
                            Netto {formatCurrency(group.totalNet)}
                            <span className="mx-2 text-white/30">·</span>
                            Brutto {formatCurrency(group.totalGross)}
                            {group.totalOpen > 0 && (
                              <>
                                <span className="mx-2 text-white/30">·</span>
                                <span className="text-orange-300/80">
                                  Offen {formatCurrency(group.totalOpen)}
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.invoices.map((invoice) =>
                      renderInvoiceRow(invoice)
                    )}
                  </Fragment>
                ))
              : invoices.map((invoice) => renderInvoiceRow(invoice))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default InvoiceListView;
